import { api } from '../api/client';
import { safeStorage } from '../utils/storage';
import { shuffle } from '../utils/random';

export interface ManagedCard {
  text: string;
  type?: 'truth' | 'dare';
}

export type DifficultyLevel = 'easy' | 'medium' | 'hard' | 'all' | null;

interface SessionContext {
  gameId: string;
  levelId: string;
  lang: string;
  difficulty: DifficultyLevel;
  sessionId: string;
  pool: ManagedCard[];
  recentlySeen: Set<string>;
  seenSet: Set<string>;
  currentPage: number;
  totalPages: number;
  hasMorePages: boolean;
  difficultyChain: DifficultyLevel[];
  exhaustedDifficulties: Set<string>;
  isFetching: boolean;
}

const RECENTLY_SEEN_BUFFER = 30;
const FETCH_THRESHOLD = 10;
const SEEN_STORAGE_PREFIX = 'pg_seen_';
const SEEN_TTL_DAYS = 30;
const MAX_SEEN_PER_LEVEL = 5000;

function getSeenStorageKey(gameId: string, levelId: string, lang: string): string {
  return `${SEEN_STORAGE_PREFIX}${gameId}_${levelId}_${lang}`;
}

interface SeenStorage {
  cards: string[];
  ts: number;
}

function loadSeenFromStorage(gameId: string, levelId: string, lang: string): Set<string> {
  try {
    const key = getSeenStorageKey(gameId, levelId, lang);
    const raw = safeStorage.get(key);
    if (!raw) return new Set();
    const data: SeenStorage = JSON.parse(raw);
    const ageMs = Date.now() - data.ts;
    if (ageMs > SEEN_TTL_DAYS * 86400 * 1000) {
      safeStorage.remove(key);
      return new Set();
    }
    return new Set(data.cards);
  } catch {
    return new Set();
  }
}

function saveSeenToStorage(gameId: string, levelId: string, lang: string, seen: Set<string>): void {
  try {
    const key = getSeenStorageKey(gameId, levelId, lang);
    const arr = Array.from(seen);
    const capped = arr.length > MAX_SEEN_PER_LEVEL ? arr.slice(-MAX_SEEN_PER_LEVEL) : arr;
    const data: SeenStorage = { cards: capped, ts: Date.now() };
    safeStorage.set(key, JSON.stringify(data));
  } catch {}
}

function generateSessionId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function shortKey(text: string): string {
  return text.toLowerCase().trim().slice(0, 80);
}

function escalateDifficulty(current: DifficultyLevel): DifficultyLevel | null {
  switch (current) {
    case 'easy':   return 'medium';
    case 'medium': return 'hard';
    case 'hard':   return null;
    case 'all':    return null;
    case null:     return null;
  }
}

class CardsManagerImpl {
  private session: SessionContext | null = null;
  private notificationHandler: ((level: DifficultyLevel) => void) | null = null;

  async startSession(
    gameId: string,
    levelId: string,
    lang: string,
    difficulty: DifficultyLevel,
  ): Promise<{ pool: ManagedCard[]; cardType: 'tod' | 'simple' }> {
    const sessionId = generateSessionId();
    const seenSet = loadSeenFromStorage(gameId, levelId, lang);

    this.session = {
      gameId,
      levelId,
      lang,
      difficulty,
      sessionId,
      pool: [],
      recentlySeen: new Set(),
      seenSet,
      currentPage: 0,
      totalPages: 1,
      hasMorePages: true,
      difficultyChain: difficulty === null || difficulty === 'all' ? [null] : [difficulty],
      exhaustedDifficulties: new Set(),
      isFetching: false,
    };

    const fetchResult = await this.fetchNextPage(seenSet);

    return {
      pool: this.session.pool.slice(),
      cardType: fetchResult.cardType,
    };
  }

  private async fetchNextPage(seenSet: Set<string>): Promise<{ cardType: 'tod' | 'simple' }> {
    const s = this.session;
    if (!s || s.isFetching) return { cardType: 'simple' };

    s.isFetching = true;
    let cardType: 'tod' | 'simple' = 'simple';

    try {
      const currentDifficulty = s.difficultyChain[s.difficultyChain.length - 1];
      const nextPage = s.currentPage + 1;

      const skipThisDifficulty =
        nextPage > s.totalPages || s.exhaustedDifficulties.has(currentDifficulty || 'all');

      if (!skipThisDifficulty) {
        const response = await api.getCards(
          s.gameId,
          s.levelId,
          s.lang,
          currentDifficulty,
          s.sessionId,
          nextPage,
        );

        cardType = response.card_type;
        s.totalPages = response.total_pages || 1;
        s.currentPage = response.page || nextPage;
        s.hasMorePages = s.currentPage < s.totalPages;

        const cards: ManagedCard[] = cardType === 'tod'
          ? (response.cards as Array<{ type: string; text: string }>).map(c => ({
              text: c.text,
              type: c.type as 'truth' | 'dare',
            }))
          : (response.cards as string[]).map(t => ({ text: t }));

        const newCards = cards.filter(c => {
          const k = shortKey(c.text);
          return !seenSet.has(k) && !s.recentlySeen.has(k);
        });

        const toAdd = newCards.length > 0 ? newCards : cards;
        s.pool.push(...shuffle(toAdd));

        if (s.currentPage >= s.totalPages) {
          s.exhaustedDifficulties.add(currentDifficulty || 'all');
          s.hasMorePages = false;
        }
      } else {
        s.exhaustedDifficulties.add(currentDifficulty || 'all');
      }

      if (s.pool.length === 0 && s.difficulty !== null && s.difficulty !== 'all') {
        const next = escalateDifficulty(s.difficultyChain[s.difficultyChain.length - 1]);
        if (next !== null && !s.exhaustedDifficulties.has(next)) {
          s.difficultyChain.push(next);
          s.currentPage = 0;
          s.totalPages = 1;
          s.hasMorePages = true;
          this.notifyDifficultyEscalation(next);
          s.isFetching = false;
          return this.fetchNextPage(seenSet);
        }

        if (!s.exhaustedDifficulties.has('all')) {
          s.difficultyChain.push(null);
          s.currentPage = 0;
          s.totalPages = 1;
          s.hasMorePages = true;
          s.isFetching = false;
          return this.fetchNextPage(seenSet);
        }
      }
    } catch (err) {
      console.error('[CardsManager] Fetch error:', err);
    } finally {
      s.isFetching = false;
    }

    return { cardType };
  }

  async getNext(): Promise<ManagedCard | null> {
    const s = this.session;
    if (!s) return null;

    if (s.pool.length <= FETCH_THRESHOLD && s.hasMorePages && !s.isFetching) {
      this.fetchNextPage(s.seenSet).catch(e =>
        console.error('[CardsManager] Background fetch failed:', e)
      );
    }

    if (s.pool.length === 0) {
      await this.fetchNextPage(s.seenSet);
    }

    if (s.pool.length === 0) {
      if (s.recentlySeen.size > 0) {
        s.recentlySeen.clear();
        s.seenSet.clear();
        await this.fetchNextPage(s.seenSet);
      }
    }

    if (s.pool.length === 0) return null;

    const card = s.pool.shift()!;
    const k = shortKey(card.text);
    s.recentlySeen.add(k);

    if (s.recentlySeen.size > RECENTLY_SEEN_BUFFER) {
      const arr = Array.from(s.recentlySeen);
      s.recentlySeen = new Set(arr.slice(-RECENTLY_SEEN_BUFFER));
    }

    s.seenSet.add(k);
    saveSeenToStorage(s.gameId, s.levelId, s.lang, s.seenSet);

    return card;
  }

  async getNextOfType(type: 'truth' | 'dare'): Promise<ManagedCard | null> {
    const s = this.session;
    if (!s) return null;

    let idx = s.pool.findIndex(c => c.type === type);
    if (idx !== -1) return this.takeFromPool(idx);

    if (s.hasMorePages && !s.isFetching) {
      await this.fetchNextPage(s.seenSet);
    }

    idx = s.pool.findIndex(c => c.type === type);
    if (idx !== -1) return this.takeFromPool(idx);

    if (s.pool.length > 0) {
      console.warn(`[CardsManager] No ${type} cards, falling back to any type`);
      return this.takeFromPool(0);
    }

    return null;
  }

  private takeFromPool(idx: number): ManagedCard {
    const s = this.session!;
    const card = s.pool.splice(idx, 1)[0];
    const k = shortKey(card.text);
    s.recentlySeen.add(k);

    if (s.recentlySeen.size > RECENTLY_SEEN_BUFFER) {
      const arr = Array.from(s.recentlySeen);
      s.recentlySeen = new Set(arr.slice(-RECENTLY_SEEN_BUFFER));
    }

    s.seenSet.add(k);
    saveSeenToStorage(s.gameId, s.levelId, s.lang, s.seenSet);

    return card;
  }

  private notifyDifficultyEscalation(newLevel: DifficultyLevel): void {
    this.notificationHandler?.(newLevel);
  }

  setNotificationHandler(handler: (level: DifficultyLevel) => void): void {
    this.notificationHandler = handler;
  }

  cleanup(): void {
    this.session = null;
  }

  getDebugInfo() {
    return this.session;
  }

  isActive(): boolean {
    return this.session !== null;
  }
}

export const cardsManager = new CardsManagerImpl();
